import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import OnDeleteIcon from '../../../icons/OnDeleteIcon';
import {
    deleteUserEvent,
    setCurrentUserEvent,
    setOpenAppointmentSchedulerPanel,
    updateUserEvent
} from '../../../store/calendarService';
import { isTimePast, reverseFormatDate } from '../../../utils/dateFunctions';
import { getConfiguredItems } from '../../../utils/directusFunctions';
import { hasRole } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import StaticField from '../../formElements/StaticField';
import TextAreaField from '../../formElements/TextAreaField';
import Panel from '../../ui/panel/Panel';
import StartAndEndTimesSelect from '../components/StartAndEndTimesSelect';
import { acceptedRoles } from '../utils/calendarUtils';
import { validateUserEvent } from '../validation/validateUserEventEdit';

import sidePanel from '../styles/schedulePanel.module.css';

const _NOTES_LIMIT = 250;

const UserEventEdit = ({ usersCurrentEvent }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        userId: '',
        userEventTypeId: '',
        startTime: '08:00',
        endTime: '09:00',
        date: String(new Date().toISOString().split('T')[0]),
        repeat: 'NO_REPEAT',
        repeatUntilDate: '',
        notes: ''
    };

    const [initialStateOnEntry, setInitialStateOnEntry] = useState({});
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(true);
    const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
    const [isDisabledSaveButton, setIsDisabledSaveButton] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const userEventTypes = useSelector((state) => state.entities.directusService.userEventTypes);
    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        setNewEntry({
            ...usersCurrentEvent,
            notes: usersCurrentEvent.notes || ''
        });
        setInitialStateOnEntry(usersCurrentEvent);
        const disabled =
            !hasRole(acceptedRoles, roles) ||
            isTimePast(`${usersCurrentEvent.date}T${usersCurrentEvent.startTime}`);
        setIsDisabled(disabled);
        setIsDeleteDisabled(disabled);
    }, []);

    useEffect(() => {
        if (!loggedInUser?.contractIds?.length || userEventTypes?.length < 1) return;
        let filtered = [];
        loggedInUser.contractIds.forEach((el) => {
            filtered = [...filtered, ...getConfiguredItems(userEventTypes, el)];
        });
    }, [userEventTypes]);

    useEffect(() => {
        setIsDisabledSaveButton(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === `User event ${newEntry.userEventType} has been updated`) {
            setIsDisabledSaveButton(false);
            onPanelClose();
        }
        if (successMessage.includes(`User event ${newEntry.userEventType} has been deleted`)) {
            setIsDisabledSaveButton(false);
            onPanelClose();
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) {
            setIsDisabledSaveButton(false);
        }
    }, [errorMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onPanelClose = () => {
        setNewEntry(initialState);
        setInitialStateOnEntry({});
        dispatch(setCurrentUserEvent({}));
        dispatch(setOpenAppointmentSchedulerPanel(false));
    };

    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateTime = (key, value) => {
        clearError(key === 'endTime' ? 'startTime' : 'endTime');
        onUpdate(key, value);
    };

    const onUpdateDate = (date) => {
        clearError('startTime');
        clearError('endTime');
        onUpdate('date', date);
    };

    const onDelete = () => {
        dispatch(deleteUserEvent(newEntry.id, newEntry.userId, newEntry.userEventType));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsDisabledSaveButton(true);
        const newErrors = validateUserEvent(newEntry, initialStateOnEntry);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateUserEvent(newEntry.id, newEntry.userEventType, newEntry));
    };

    // RENDER
    return (
        <Panel width="500px" open={openAppointmentSchedulerPanel} onToggle={onPanelClose}>
            <div className={sidePanel.panelAction}>
                <OnDeleteIcon onDelete={onDelete} active={!!newEntry.id && !isDeleteDisabled} />
            </div>

            <div className={sidePanel.formWrapper}>
                <form onSubmit={onSubmit} data-testid="user_event_edit_form">
                    <StaticField
                        id="userEventTypeId"
                        label="Event Type"
                        content={newEntry.userEventType}
                    />

                    {isDisabled ? (
                        <>
                            <StaticField
                                id="startTime"
                                label="Start Time"
                                content={newEntry.startTime}
                            />
                            <StaticField id="endTime" label="End Time" content={newEntry.endTime} />
                        </>
                    ) : (
                        <StartAndEndTimesSelect
                            startTime={newEntry.startTime}
                            endTime={newEntry.endTime}
                            onTimeChange={onUpdateTime}
                            disabled={isDisabled}
                            errors={errors}
                        />
                    )}

                    {isDisabled ? (
                        <StaticField
                            id="date"
                            label="Date"
                            content={reverseFormatDate(newEntry.date)}
                        />
                    ) : (
                        <DateSelect
                            mandatory={true}
                            value={newEntry.date}
                            error={errors.date}
                            disabled={isDisabled}
                            onDateChange={(date) => onUpdateDate(date)}
                        />
                    )}

                    {isDisabled ? (
                        <StaticField id="notes" label="Event Notes" content={newEntry.notes} />
                    ) : (
                        <TextAreaField
                            id="notes"
                            label="Event Notes"
                            placeholder={'Enter event notes...'}
                            disabled={isDisabled}
                            maxLength={_NOTES_LIMIT}
                            value={newEntry.notes}
                            count={`${newEntry.notes?.length || 0}/${_NOTES_LIMIT}`}
                            onChange={(e) => onUpdate('notes', e.target.value)}
                        />
                    )}

                    <div className={sidePanel.updateButton}>
                        <Button
                            id="appointmentEditButton"
                            content="Update"
                            disabled={isDisabledSaveButton || isDisabled}
                            error={errors.button}
                            clearError={() => clearError('button')}
                            customErrorClass="rightSideButtonError"
                        />
                    </div>
                </form>
            </div>
        </Panel>
    );
};

UserEventEdit.propTypes = {
    usersCurrentEvent: PropTypes.object
};

export default UserEventEdit;
